/**
 * 묶음(번들) 옵션 서비스
 *
 * 화면(PricingEdit.vue 의 번들 추가 버튼)과 자동 작업 경로(AIRocketReg.vue cliPageSetBundle mode:'add')가
 * **같은 계산**으로 묶음 옵션을 만들도록 로직만 떼어낸 모듈이다. Vue 인스턴스에 의존하지 않는다.
 *
 * ⚠ 가격 설정은 반드시 인자로 넘긴다.
 *   화면은 product.pricing.info 를 1순위로 읽고 AIRocketReg 는 defaultSettings 를 쓰기 때문에,
 *   this 에 의존해 옮기면 사람이 만든 묶음과 도구가 만든 묶음의 가격이 갈린다.
 *   순서 판단 자체는 pricingPolicy 가 갖는다 — 여기서 따로 고르지 않는다.
 */

import pricingPolicy from './pricingPolicy';
import { normalizeBoxDimension, normalizeBoxWeight, BOX_FLOOR_DIMENSION } from './packageEstimate';
import { hasBundleCount } from './seoSpecGuard';
import { restoreUserEdits } from './userEditGuard';

// 등록서류(imagePage.images.labelImages)에서 한글표시사항 라벨을 가리키는 종류 이름.
// koreanLabelService 가 라벨을 넣을 때 쓰는 값과 같은 문자열이다.
const KOREAN_LABEL_IMAGE_TYPE = '제품 한글 표시사항 라벨 또는 도안 이미지';

export default {
  // 깊은 복사 (PricingEdit.deepClone 과 같은 동작)
  deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    const result = Array.isArray(obj) ? [] : {};

    for (let key of Object.keys(obj)) {
      result[key] = this.deepClone(obj[key]);
    }

    return result;
  },

  /**
   * 가격 설정 풀기 — 판단은 pricingPolicy 하나가 한다 (상품 설정 → 화면 기본세팅 순).
   * 여기서는 묶음 계산에만 필요한 msrp 항목만 얹는다.
   *
   * @param {Object} product - 상품 객체
   * @param {Object} screenSettings - 화면 기본세팅 (PricingEdit 의 settings / AIRocketReg 의 defaultSettings)
   * @returns {Object} 묶음 계산에 필요한 설정 전체
   */
  resolveBundleSettings(product, screenSettings) {
    const screen = screenSettings || {};
    const policy = pricingPolicy.resolve(product, screen);

    return {
      ...policy,
      // msrp 는 화면에서도 상품 설정이 아니라 기본세팅에서만 읽는다 (PricingEdit.calculateMsrpValue)
      msrpRoundUnit: screen.roundUnit || policy.roundUnit || 100
    };
  },

  // 옵션 표시명 (korName → optionName → option1/option2 조합)
  optionDisplayName(option) {
    if (!option) return '기본 옵션';

    if (option.korName) return option.korName;
    if (option.optionName) return option.optionName;

    let name = '';
    if (option.option1) {
      name += option.option1.nameTrans || option.option1.name || '';
    }
    if (option.option2) {
      name += ' / ' + (option.option2.nameTrans || option.option2.name || '');
    }
    return name || '기본 옵션';
  },

  // 기존 "(N개묶음)" 표기 제거 — 두 번 묶었을 때 표기가 누적되는 걸 막는다
  stripBundleSuffix(name) {
    return String(name || '').replace(/\s*\(\d+개묶음\)\s*$/, '');
  },

  // "(N개묶음)" 표기 붙이기 — 묶음 옵션명을 만드는 유일한 규칙이다.
  // 작업개시(seoService)가 옵션명을 다시 쓸 때도 이 함수를 불러서 표기를 되살린다.
  withBundleSuffix(name, bundleQuantity) {
    return `${this.stripBundleSuffix(name)} (${bundleQuantity}개묶음)`;
  },

  // 묶음 수 N 에 맞춘 옵션명 — N≥2 면 "(N개묶음)" 을 붙이고(기존 꼬리는 바꿈), 1 이면 뗀다.
  // 화면 묶음 단추(PricingEdit)와 MCP setBundle(mode:'apply')가 같이 부른다(PR261021-C6B4A5).
  bundledOptionName(name, bundleQuantity) {
    const n = Number(bundleQuantity) || 1;
    return n >= 2 ? this.withBundleSuffix(name, n) : this.stripBundleSuffix(name);
  },

  /** 상품 전체 묶음 수 N — 소싱 묶음(sourcingBundleQuantity) 먼저, 없으면 옵션 bundleQuantity 가 전부 같은 ≥2 값일 때만(다르면 없음 · 지어내지 않음).
   *  SEO 가 이 N 을 상품명에 넣는다(seoService 프롬프트 «Bundle Quantity» · ensureBundleCount) */
  productBundleQuantity(product) {
    const s = Number(product && product.sourcingBundleQuantity);
    if (Number.isInteger(s) && s >= 2) return s;
    const qs = [...new Set(((product && product.options) || []).map(o => Number(o && o.bundleQuantity) || 1))];
    return qs.length === 1 && qs[0] >= 2 ? qs[0] : null;
  },

  // 상품명에 이 묶음 수가 이미 들어 있나 — 상품 전체 묶음 수가 N 일 때만 상품명 속 N(N개·N세트·N개 세트)을 묶음 수로 본다.
  // 섞인 상품(1개 + 2개묶음)의 상품명 속 수는 옵션 자기 수일 수 있다(«화이트 2세트» 의 2개묶음 = 4세트) — 그땐 아니다(윈 3452 PR261021-56DBDD · 총괄 반례)
  productNameHasBundle(productName, product, bundleQuantity) {
    const n = Number(bundleQuantity);
    return this.productBundleQuantity(product) === n && hasBundleCount(String(productName || ''), n);
  },

  // 허브 상품명(«상품명, 옵션명») 의 옵션명 — 상품명에 이 묶음 수가 이미 있으면 옵션명 끝 «(N개묶음)» 을 뗀다
  hubOptionName(productName, korName, product) {
    const m = String(korName || '').match(/\((\d+)개묶음\)\s*$/);
    if (m && this.productNameHasBundle(productName, product, Number(m[1]))) return this.stripBundleSuffix(korName);
    return korName;
  },

  // 묶음 옵션번호 = 원본 + "B" + 수량. 순번이 아니므로 배열 끝에 붙여도 기존 번호가 밀리지 않는다.
  bundleOptionNumber(originalOptionNumber, bundleQuantity) {
    return `${originalOptionNumber}B${bundleQuantity}`;
  },

  /**
   * 묶음 옵션인지 판별하고 원본번호·수량을 돌려준다. bundleOptionNumber 의 반대 방향이다.
   *
   * 플래그를 1순위로 보되 옵션번호("원본B수량")까지 같이 본다 (가격 화면도 같은 규칙을 쓴다).
   *
   * ⚠ 옵션번호 폴백은 **옛 데이터를 받는 그물**이다. 플래그가 지워지던 근본 원인
   * (PricingEdit.initializeCalculatedOptions 가 묶음 플래그 없는 객체를 만들고
   * savePricing 이 그걸 `|| false` 로 굳히던 것)은 고쳤지만, 그 전에 저장된 건 그대로다.
   *
   * 실측(2026-09-18): 번호에 B 가 붙은 묶음 옵션("...001B2")이 `isBundleOption: false`,
   * `originalOptionNumber: null` 로 저장돼 있는 상품이 실제로 있었다
   * (`bundleQuantity` 만 2 로 남아 있었다 — 얇은 객체에 그 필드는 있기 때문이다).
   * ⇒ 그런 옵션은 옵션번호가 묶음이라는 유일한 단서다. 폴백을 지우지 말 것.
   *
   * @param {Object} option - 옵션 객체
   * @returns {Object|null} { originalOptionNumber, bundleQuantity } 또는 묶음이 아니면 null
   */
  resolveBundleInfo(option) {
    if (!option || !option.optionNumber) return null;

    const matched = String(option.optionNumber).match(/^(.+)B(\d+)$/);
    if (!matched && !option.isBundleOption) return null;

    const bundleQuantity = Number(option.bundleQuantity) || (matched ? Number(matched[2]) : 0);
    if (!bundleQuantity || bundleQuantity < 2) return null;

    return {
      originalOptionNumber: option.originalOptionNumber || (matched ? matched[1] : null),
      bundleQuantity: bundleQuantity
    };
  },

  /**
   * 표기(옵션명·한글표시 제품명)용 묶음 판별 — resolveBundleInfo + «원본 자리 묶음».
   *
   * 소싱 묶음 수량(applySourcingBundleQuantity)·setBundle apply·자동 번들은 원본 옵션을 그 자리에서 묶음으로 바꾼다:
   * 번호에 B 없음 · isBundleOption false · bundleQuantity N(가격도 N개 값). resolveBundleInfo 는 이걸 못 알아봐
   * «N개묶음» 표기가 빠졌다(10-21 윈 yewoncoltd 261021018001 · 10개 묶음).
   * ⚠ 저장 판별(resolveBundleInfo → PricingEdit.bundleFieldsOf 가 isBundleOption 으로 굳힘)에는 쓰지 말 것 —
   * 원본 자리 묶음을 묶음 옵션으로 저장하면 서류를 다시 만들 때마다 무게·수량이 거듭 곱해진다(buildBundleJsonDocument).
   *
   * @param {Object} option - 옵션 객체
   * @returns {Object|null} { originalOptionNumber, bundleQuantity } 또는 묶음이 아니면 null
   */
  bundleLabelInfo(option) {
    const info = this.resolveBundleInfo(option);
    if (info) return info;
    if (!option || !option.optionNumber) return null;
    const bundleQuantity = Number(option.bundleQuantity);
    if (!Number.isInteger(bundleQuantity) || bundleQuantity < 2) return null;
    return { originalOptionNumber: null, bundleQuantity: bundleQuantity };
  },

  /**
   * 견적서 옵션 묶음 수 — 표기 잣대(bundleLabelInfo · 원본 자리 포함) 또는 가격 계산 결과(pricing.options 같은 번호의 bundleQuantity 2+). 묶음 아니면 1.
   * 상품 플래그(pricing.bundleInfo.hasBundling)는 상품 하나 값이고 가격 계산 최댓값이 2+ 일 때만 켜져 원본 자리 묶음을 못 본다
   * (견적서가 «1개·1켤레» 로 채움 · PR261021-DB0DBE) — koreanLabelService.labelProductNameFor 와 같은 까닭으로 쓰지 않는다.
   */
  quotationBundleQuantity(product, option) {
    const info = this.bundleLabelInfo(option);
    if (info) return info.bundleQuantity;
    const num = option && option.optionNumber;
    const priced = num && product && product.pricing && Array.isArray(product.pricing.options)
      ? product.pricing.options.find(p => p && p.optionNumber === num) : null;
    const q = Number(priced && priced.bundleQuantity);
    return Number.isInteger(q) && q >= 2 ? q : 1;
  },

  /** 견적서 상품 단위 묶음 요약 — 옵션마다 quotationBundleQuantity · same = 모두 같은 N(2+)|null · mixed = 2+ 와 다른 수가 섞임 · max */
  productBundleSummary(product) {
    const byOption = {};
    for (const o of (product && product.options) || []) {
      if (o && o.optionNumber) byOption[o.optionNumber] = this.quotationBundleQuantity(product, o);
    }
    const qs = Object.values(byOption);
    const max = qs.length ? Math.max(...qs) : 1;
    const same = max >= 2 && qs.every(q => q === max) ? max : null;
    return { byOption, same, mixed: max >= 2 && !same, max };
  },

  /** 견적서 상품 단위 글(상품명 꼬리 · 속성 프롬프트 안내 · 물류 안내) — productBundleSummary 하나로 가른다 */
  quotationBundleNotes(product) {
    const summary = this.productBundleSummary(product);
    if (summary.same) {
      const text = `${summary.same}개 묶음`;
      return {
        nameSuffix: ` (${text})`,
        note: `\n⚠️ 중요: 이 상품은 ${text}으로 판매됩니다. 속성 생성 시 이를 반드시 고려하세요.`,
        logisticsNote: `\n⚠️ 번들 상품: ${text}으로 판매되므로, 박스 크기와 무게는 번들 수량(${summary.same}개)을 고려하여 계산하세요.`,
        summary
      };
    }
    if (summary.mixed) {
      const list = Object.entries(summary.byOption).filter(([, q]) => q >= 2).map(([n, q]) => `${n}: ${q}개 묶음`).join(', ');
      return {
        nameSuffix: '',
        note: `\n⚠️ 중요: 이 상품은 옵션마다 묶음 수량이 다릅니다(${list} · 나머지는 1개). 공통 속성에는 묶음 수량을 넣지 마세요 — 수량은 옵션별 속성에서 정합니다.`,
        logisticsNote: `\n⚠️ 번들 상품: 옵션마다 묶음 수량이 달라, 박스 크기와 무게는 가장 큰 묶음(${summary.max}개)을 담을 수 있게 계산하세요.`,
        summary
      };
    }
    return { nameSuffix: '', note: '', logisticsNote: '', summary };
  },

  /** 견적서 옵션 상품명(startPage.productName) — 그 옵션이 묶음이면 «(N개 묶음)» · 이미 그 수가 적혀 있으면 그대로(labelProductNameFor 와 같은 중복 규칙) */
  quotationOptionProductName(product, option, baseName) {
    const name = String(baseName || '');
    const n = this.quotationBundleQuantity(product, option);
    if (n < 2) return name;
    const stripped = this.stripBundleSuffix(name);
    if (stripped !== name || name.includes(`(${n}개 묶음)`) || this.productNameHasBundle(stripped, product, n)) return name;
    return `${name} (${n}개 묶음)`;
  },

  // MSRP 값 계산
  calculateMsrpValue(sellingPrice, settings) {
    const multiplier = settings.msrpMultiplier || 1.3;
    const roundUnit = settings.msrpRoundUnit || settings.roundUnit || 100;
    return Number(Math.round((sellingPrice * multiplier) / roundUnit) * roundUnit);
  },

  // 스키마에 osrp 필드가 있으면 msrp 대신 그쪽을 쓴다
  shouldUseOsrp(docObj) {
    try {
      return Object.prototype.hasOwnProperty.call(docObj.productPage.commonAttributes, 'osrp');
    } catch (error) {
      return false;
    }
  },

  /**
   * 묶음 옵션 객체 생성 — 원본 옵션을 깊은 복사하고 가격·이름·재고·번호만 바꾼다.
   *
   * 이미지·인증정보는 승계한다(같은 물건 N개라 사진이 같고, 승계가 없으면 묶음 옵션이
   * 처음부터 미완료로 떨어진다). 다만 **한글표시사항은 무효화한다** — 승계되는 라벨은
   * 1개짜리다. invalidateKoreanLabel 주석 참고.
   *
   * @param {Object} originalOption - 원본 옵션 (product.options 의 원소 또는 화면의 계산용 옵션)
   * @param {Number} bundleQuantity - 묶음 수량
   * @param {Object} settings - resolveBundleSettings 결과
   * @param {String} originalOptionName - 원본 옵션 표시명 (부르는 쪽이 정한다)
   * @returns {Object} 묶음 옵션
   */
  buildBundleOption(originalOption, bundleQuantity, settings, originalOptionName) {
    // 원본 옵션 전체 깊은 복사 (이미지·한글표시사항·작업 상태가 그대로 따라온다)
    // — 한글표시사항만 아래에서 걷어낸다.
    const bundleOption = this.deepClone(originalOption);

    bundleOption.optionNumber = this.bundleOptionNumber(originalOption.optionNumber, bundleQuantity);

    const baseName = originalOptionName === undefined ? this.optionDisplayName(originalOption) : originalOptionName;
    bundleOption.korName = this.withBundleSuffix(baseName, bundleQuantity);

    bundleOption.bundleQuantity = bundleQuantity;

    // 매입가 = 위안 단가 × 환율 × 수량 (위안 단가 자체는 원본 그대로 둔다). 통합통관 모드면 소싱과 같은 공식
    const purchasePrice = pricingPolicy.purchaseCost(originalOption.price * bundleQuantity, settings);
    bundleOption.purchasePrice = Math.round(purchasePrice);

    // 공급가
    let supplyPrice;
    if (settings.enableMinSupplyMargin) {
      // 최소 공급 마진 보장 모드
      supplyPrice = purchasePrice + settings.minSupplyMarginAmount;
    } else {
      // 일반 마진율 모드
      supplyPrice = purchasePrice / (1 - settings.marginRate / 100);
    }
    bundleOption.supplyPrice = Math.round(supplyPrice / settings.roundUnit) * settings.roundUnit;

    // 판매가
    const sellingPrice = bundleOption.supplyPrice / (1 - settings.coupangMarginRate / 100);
    bundleOption.sellingPrice = Math.round(sellingPrice / settings.roundUnit) * settings.roundUnit;

    // 마진
    bundleOption.supplyMargin = bundleOption.supplyPrice - bundleOption.purchasePrice;
    bundleOption.sellingMargin = bundleOption.sellingPrice - bundleOption.supplyPrice;

    // 재고 (원본 재고 / 묶음수량, 내림)
    bundleOption.stock = Math.floor(originalOption.stock / bundleQuantity);

    bundleOption.isBundleOption = true;
    bundleOption.originalOptionNumber = originalOption.optionNumber;

    // 승계된 라벨은 1개짜리다 — 만드는 시점에 무효화한다.
    this.invalidateKoreanLabel(bundleOption);

    return bundleOption;
  },

  /**
   * 묶음 옵션의 한글표시사항을 무효화한다 — 「다시 만들어야 한다」로 보이게 만드는 것이 목적이다.
   *
   * 승계된 라벨은 원본(1개)을 보고 만든 것이라, 2개를 보내면서 내용량이 1개로 적힌 라벨이 붙는다.
   * 표시사항은 규제 대상이라 **조용히 틀린 라벨**보다 미완료로 보이는 쪽이 낫다 —
   * 사람이 알아채고, 작업이 안 끝난 상품은 도구의 등록전송 대상에서도 빠진다.
   *
   * ⚠ 옵션만 비우면 등록전송 가드는 안 걸린다. 그 가드는 **상품 레벨** koreanLabelStatus 만 보므로
   *   부르는 쪽이 상품 상태도 같이 내려야 한다 (AIRocketReg.cliPageAddBundleOptions / PricingEdit.savePricing).
   * ⚠ 이미지·인증정보는 건드리지 않는다. 그건 여전히 승계돼야 하는 성질이다.
   *
   * 되살리는 길은 한글표시사항 작업을 다시 돌리는 것 하나다 (옵션 전체를 다시 만든다).
   *
   * @param {Object} option - 묶음 옵션
   * @returns {Object} 같은 옵션 객체
   */
  invalidateKoreanLabel(option) {
    if (!option) return option;

    option.koreanLabels = null;
    option.koreanLabelImage = null;
    option.koreanLabelStatus = null;

    return option;
  },

  /**
   * 묶음 등록서류에 승계된 한글표시사항 라벨 이미지를 비운다.
   *
   * 옵션 상태만 내려도 화면에는 미완료로 보이지만, 서류에는 1개짜리 라벨 이미지가 그대로 남는다.
   * 사람이 화면에서 골라 전송하면 그게 쿠팡으로 간다 — 그래서 서류에서도 같이 비운다.
   * 라벨 작업을 다시 돌리면 koreanLabelService 가 이 자리에 새 라벨을 넣는다.
   *
   * ⚠ **항목을 지우지 않고 파일 주소만 비운다.** 지우면 라벨을 다시 만들 때
   *   koreanLabelService(applyBatchLabelResults)가 화면 메모리 기준으로 "몇 번째 항목" 을 찾아
   *   `labelImages.0.labelImageFiles` 로 쓰기 때문에, 서류에서 항목이 사라져 있으면 종류 이름이
   *   빠진 항목이 새로 생긴다. 2026-09-19 실측 — 라벨은 다시 만들어졌는데 서류에는
   *   `{labelImageFiles: ...}` 만 남아 라벨이 없는 것으로 읽혔다.
   *   자리(종류 이름)를 남겨 두면 어느 경로로 와도 제자리에 채워진다.
   *
   * ⚠ **묶음 옵션을 만드는 시점에만** 부른다. 이미 저장된 묶음 서류를 다시 만드는 경로
   *   (PricingEdit.savePricing 은 저장할 때마다 묶음 서류를 새로 만든다)에서 부르면
   *   다시 만들어 붙인 라벨을 도로 지운다.
   *
   * @param {Object} doc - buildBundleJsonDocument 결과
   * @returns {Object} 같은 서류 객체
   */
  clearKoreanLabelImageInDoc(doc) {
    const images = doc && doc.imagePage && doc.imagePage.images;
    if (!images || !Array.isArray(images.labelImages)) return doc;

    // 라벨 종류 이름은 koreanLabelService(applyBatchLabelResults 등)가 쓰는 것과 같아야 한다.
    // 어긋나면 여기서 아무것도 못 비우고 1개짜리 라벨이 조용히 남는다.
    images.labelImages.forEach(img => {
      if (img && img.labelImageType === KOREAN_LABEL_IMAGE_TYPE) img.labelImageFiles = '';
    });

    return doc;
  },

  /**
   * 묶음 옵션의 쿠팡 등록서류 생성 — 원본 서류를 깊은 복사하고 가격 3종과 노출속성 옵션명만 바꾼다.
   * imagePage / legalPage 는 손대지 않는다 (같은 물건 N개라 이미지·인증정보가 같다).
   *
   * @param {Object} originalDoc - 원본 옵션의 jsonDocument
   * @param {Object} bundleOption - buildBundleOption 결과
   * @param {String} originalOptionName - 노출속성에서 찾아 바꿀 원본 옵션명
   * @param {Object} settings - resolveBundleSettings 결과
   * @param {Array|null} edits - 상품 quotationUserEdits — 사람이 견적서에서 고친 묶음 옵션 노출속성은 되돌려 둔다(saram 판 #737 ③)
   * @returns {Object} 묶음 옵션의 jsonDocument
   */
  buildBundleJsonDocument(originalDoc, bundleOption, originalOptionName, settings, edits) {
    const bundleDoc = this.deepClone(originalDoc);

    if (bundleDoc.productPage && bundleDoc.productPage.commonAttributes) {
      bundleDoc.productPage.commonAttributes.purchasePrice = bundleOption.supplyPrice;
      bundleDoc.productPage.commonAttributes.coupangSalePrice = bundleOption.sellingPrice;

      const msrpValue = this.calculateMsrpValue(bundleOption.sellingPrice, settings);
      const fieldName = this.shouldUseOsrp(bundleDoc) ? 'osrp' : 'msrp';
      bundleDoc.productPage.commonAttributes[fieldName] = msrpValue;

      console.log(`✅ 번들 옵션 가격 업데이트: ${bundleOption.optionNumber} (공급가: ${bundleOption.supplyPrice}, 판매가: ${bundleOption.sellingPrice})`);

      // 노출속성 — 원본 옵션명을 포함하는 속성값을 묶음 옵션명으로 바꾼다
      if (bundleDoc.productPage.commonAttributes.exposedAttributes) {
        bundleDoc.productPage.commonAttributes.exposedAttributes.forEach(attr => {
          if (attr.attributeValue && typeof attr.attributeValue === 'string') {
            if (originalOptionName && attr.attributeValue.includes(originalOptionName)) {
              attr.attributeValue = bundleOption.korName;
            } else if (attr.attributeName === '수량') {
              attr.attributeValue = this.bundleQuantityValue(attr.attributeValue, bundleOption.bundleQuantity);
            }
          }
        });
        // 가격 저장마다 묶음 서류를 다시 만든다 — 사람이 고친 값은 이름 바꿔 끼우기·수량 곱하기가 덮지 않게
        restoreUserEdits(bundleDoc.productPage.commonAttributes.exposedAttributes, bundleOption.optionNumber, edits)
          .forEach(x => console.log(`✋ 묶음 ${bundleOption.optionNumber} «${x.attributeName}» 사람 값 «${x.to}» 유지`));
      }
    }

    this.scaleBundlePackage(bundleDoc.logisticsPage, bundleOption.bundleQuantity);

    return bundleDoc;
  },

  // 노출속성 «수량» — 단품 «1개»·«2개» 를 묶음 수만큼 곱한다(10-20 jaemaro 0D6EF9 · 10개 묶음인데 «1개»). 숫자가 없으면 «N개».
  bundleQuantityValue(value, bundleQuantity) {
    const n = Number(bundleQuantity);
    if (!(n > 1)) return value;
    const m = /^\s*(\d+)\s*(.*)$/.exec(String(value || ''));
    return m ? `${Number(m[1]) * n}${m[2] || '개'}` : `${n}개`;
  },

  // 포장 무게·크기 — 단품 값을 그대로 복사하면 견적서가 «저장값 유지»로 지켜 10개 묶음이 단품 7g 로 나갔다(10-20 jaemaro 0D6EF9·5EE565).
  // 무게는 × 묶음 수, 크기는 가장 얇은 변 × 묶음 수(쌓기). 빈 칸·하한 자리값(100*100*100/100·1000)은 «값 없음»이라 두고 견적서가 채운다.
  scaleBundlePackage(lp, bundleQuantity) {
    const n = Number(bundleQuantity);
    if (!lp || !(n > 1)) return;
    if (lp.skuUnitBoxDimension === BOX_FLOOR_DIMENSION && ['100', '1000'].includes(String(lp.skuUnitBoxWeight))) return;
    const g = normalizeBoxWeight(lp.skuUnitBoxWeight);
    if (g) lp.skuUnitBoxWeight = String(Number(g) * n);
    const dim = normalizeBoxDimension(lp.skuUnitBoxDimension);
    if (dim) {
      const sides = dim.split('*').map(Number);
      sides[sides.indexOf(Math.min(...sides))] *= n;
      lp.skuUnitBoxDimension = sides.join('*');
    }
  }
};
